import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styles from "./index.module.scss";
import { endPoints, PORTAL } from "../../constants/urlConstants";
import { getCall } from "../../services/apiService";
import {
  getItemInLocalStorage,
  setItemInLocalStorage,
} from "../../services/localStorage";
import { hasPermission } from "../../utils/roleBasedAccess";
import { noData, swapTypeOptions } from "../../constants";
import starIcon from "../../assets/images/starIcon.svg";
import shuffle from "../../assets/images/shuffle-img.svg";
// Components
import DataGridWithPagination from "../../common/components/DataGridWithPagination";
import Loader from "../../common/components/Loader";
import DashboardHeader from "../../common/components/DashboardHeader";
import AdminFilterOverlay from "../../components/components/AdminFilterOverlay";
import AdminKpiData from "../../common/components/AdminKpiData";
import ReviewOverlay from "../../components/RatingOverLay";
import SwapFilterOverlay from "../../common/components/SwapFilterContent";
import KebabMenu from "../../common/components/KebabMenu";
import FilterPills from "../filterItemsDisplay";
import chatTearDrop from "../../assets/images/ChatTeardropDots.svg";
import defaultProfileIcon from "../../assets/images/default-profile.svg";
import CancelRegistrationOverlay from "../../common/components/CancelRegistrationOverlay";

// Types
type HdbSeekersListAdminProps = {
  filters?: any;
};
import MahatriaBlessCard from "../../common/components/MahatriaBlessCard";
import { ApiService } from "../../services/mockService";
import { Tooltip } from "@mui/material";
import DownloadPopup from "../DownloadPopUp";
import { downloadExcelFromApi } from "../../utils/downloadExcel";
import { downloadPdf } from "../../utils/downloadPdf";
import { colorizeMahatriaInfinitheism } from "../../common/components/ColorizeMahatriaInfinitheism";
import { transformSeekerResponse } from "../../utils/dataMapper";
import UserCardOverlay from "../../common/components/UserCardOverlay";
import BulkEmailsPopUp from "../BulkEmailsPopUp";
import { useDispatch, useSelector } from "react-redux";
import { getFilterConfig } from "../../utils/hdbDashboardUtils";
import {
  setFilterOptions,
  setSelectedKpiOption,
  setFilterConfig,
  setSelectedKpiTab,
  setActiveTab,
  setSelectedKpiFilter,
  resetProgramState,
  setProgramName,
} from "../../reducers/ProgramReducer";
import { RootState } from "../../store";
import { formatDateString, formateYearDate } from "../../utils/commonFunctions";
import { SortState } from "../../types/seatApproval";
import SideDrawerOverlay from "../../components/SideOverLay";
import QuickViewOverlay from "../../components/QuickViewOverlay";
import ImagePreview from "../../common/components/ImagePreview";
import ViewDetailsIcon from "../../assets/images/FullDetailsPage.svg";
import SeekerTags from "../../components/SeekerTags";

const getFiltersByContext = (selectedKpiOption, selectedKpiTab) => {
  const filterConfig = getItemInLocalStorage("filterConfig")?.data;

  if (!selectedKpiOption || !filterConfig?.sideFilterSets) return {};

  const { baseSets, contextualFilters } = filterConfig.sideFilterSets;

  const parentKey = selectedKpiOption.value;
  // Use selectedKpiTab instead of first kpiOption
  const childKey = selectedKpiTab?.value;

  const allowedFilterTypes = contextualFilters[parentKey]?.[childKey] || [];
  const finalFilters = allowedFilterTypes.reduce((acc, filterType) => {
    if (baseSets[filterType]) {
      acc.push(...baseSets[filterType]);
    }
    return acc;
  }, []);

  return finalFilters;
};

const transformFilters = (filters: any) => {
  const transformed: any = {};

  Object.entries(filters).forEach(([key, filterData]: [string, any]) => {
    if (filterData && typeof filterData === 'object') {
      if (Array.isArray(filterData.value)) {
        // Handle array of value-label objects
        transformed[key] = filterData.value.map((item: any) => item.value);
      } else if (typeof filterData.value === 'object' && 'operator' in filterData.value) {
        // Handle operator-based filters
        transformed[key] = {
          operator: filterData.value.operator,
          value: filterData.value.value
        };
      } else if (typeof filterData.value === 'object' && 'startDate' in filterData.value) {
        // Handle date range
        transformed[key] = filterData.value;
      } else {
        // Handle simple value
        transformed[key] = filterData.value;
      }
    }
  });

  return transformed;
};

const HdbSeekersListAdmin: React.FC<HdbSeekersListAdminProps> = (props) => {
  // Router
  const navigate = useNavigate();
  const location = useLocation();
  const { programId } = useParams<{ programId: string }>();
  const programIdNumber = programId ? Number(programId) : undefined;
  const sessionId = location.state?.sessionId ?? undefined;
  const { filters } = props;
  const [bulkEmailUrl, setBulkEmailUrl] = useState<{
    programId?: number;
    filters?: { kpiFilter?: string; kpiCategory?: string };
  } | null>(null);
  const [programTypeKey, setProgramTypeKey] = useState<string>("");
  const [programCode, setProgramCode] = useState<string>("");
  const [programStartsAtDate, setProgramStartsAtDate] = useState<string>("");
  const selectedKpiTab = useSelector(
    (state: RootState) => state.ProgramReducer.selectedKpiTab,
  );
  const [sessionData, setSessionData] = useState<any[]>([]);
  const [showCancelOverlay, setShowCancelOverlay] = useState(false);
  const [sortState, setSortState] = useState<SortState>({
    sortKey: '',
    sortOrder: 'ASC'
  });
  // Extract kpiCategory and kpiFilter if present
  const initialKpiFilter =
    filters && filters.kpiCategory && filters.kpiFilter
      ? { kpiCategory: filters.kpiCategory, kpiFilter: filters.kpiFilter }
      : null;


  // Remove kpiCategory and kpiFilter from appliedFilters if present
  const initialAppliedFilters =
    filters && typeof filters === "object"
      ? Object.keys(filters).reduce((acc, key) => {
        if (key !== "kpiCategory" && key !== "kpiFilter") {
          acc[key] = filters[key];
        }
        return acc;
      }, {} as any)
      : {};

  const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters);

  const [data, setData] = useState<any[]>([]);
  const [dashboardFilters, setDashboardFilters] = useState<any>({});
  const [columns, setColumns] = useState<any[]>([]);
  const [totalData, setTotalData] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState({ value: "", open: false });
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilterOverlay, setShowFilterOverlay] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [activeOverlayType, setActiveOverlayType] = useState<
    "review" | "swap" | "quickView" | "seekerTag" | null
  >(null);
  const [dynamicProgramOptions, setDynamicProgramOptions] = useState<any[]>([]);
  const [reviewInitialRatings, setReviewInitialRatings] = useState<{
    [key: string]: { rating: number; id?: number };
  }>({});
  const [reviewInitialComments, setReviewInitialComments] = useState("");
  const [isIntialLoading, setIsintialLoading] = useState(true);
  const initalLoadingRef = useRef<boolean>(true);
  const [kpis, setKpis] = useState<any[]>([]);
  const [hasSyncedTab, setHasSyncedTab] = useState(false);
  const [subProgramsData, setSubProgramsData] = useState([]);
  const [flippedUserId, setFlippedUserId] = useState<number | null>(null);
  const [reviewInitialRecommendations, setReviewInitialRecommendations] =
    useState<any>(null);
  const [prevRating, setPrevRating] = useState<any>(null); // Add this new state
  const dispatch = useDispatch();
  // State for export popup
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [selectedOption, setSelectedOption] = useState<
    "filtered" | "all" | "download"
  >("download");
  const [loadingState, setLoadingState] = useState(false);
  const [open, setOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = React.useState({
    image: '',
    altText: '',
  });
  // State for bulk emails
  const [showBulkEmailPopup, setShowBulkEmailPopup] = useState(false);
  const activeTab = useSelector(
    (state: RootState) => state?.ProgramReducer?.activeTab,
  );
  // Get selectedKpiOption and filterConfig from Redux
  const selectedKpiOption = useSelector(
    (state: RootState) => state.ProgramReducer.selectedKpiOption,
  );
  const filterConfig = useSelector(
    (state: RootState) => state.ProgramReducer.filterConfig,
  );
  const selectedKpiFilter = useSelector(
    (state: RootState) => state.ProgramReducer.selectedKpiFilter,
  );

  const [isSubProgramsLoaded, setIsSubProgramsLoaded] = useState(false);

  useEffect(() => {
    setSearchValue((prev) => ({ ...prev, open: false, value: "" }));
  }, [activeTab, appliedFilters]);

  useEffect(() => {
    if (selectedKpiOption) {
      // Get contextual filters when KPI option changes
      const contextualFilters = getFiltersByContext(
        selectedKpiOption,
        selectedKpiTab,
      );
      setDashboardFilters(contextualFilters);
    }
  }, [selectedKpiOption, filterConfig, selectedKpiTab]);

  useEffect(() => {
    const fetchFilteringData = async () => {
      const filterResponse = getItemInLocalStorage("filterConfig");
      const storedProgramId = getItemInLocalStorage("programId");

      let options = [];
      if (filterResponse && storedProgramId && storedProgramId === programId) {
        const parentOptions = filterResponse?.data?.parentOptions || [];
        options = parentOptions.map((option) => ({
          label: option.label,
          value: option.value,
          kpiOptions: option.kpiOptions,
        }));
      } else {
        const config = await getFilterConfig(programId, dispatch);
        setItemInLocalStorage("programId", programId);
        if (config?.data?.parentOptions) {
          options = config.data.parentOptions.map((option) => ({
            label: option.label,
            value: option.value,
            kpiOptions: option.kpiOptions,
          }));
        }
        dispatch(setFilterConfig(config.data));
        if(!filters)
        {
          dispatch(setSelectedKpiTab(options[0].kpiOptions[0]));
        }
        // Add this action to your reducer
      }

      // Dispatch filterOptions to Redux instead of local state
      dispatch(setFilterOptions(options));

      // Set first option as default if options exist and no option is selected
      if (options.length > 0) {
        dispatch(
          setSelectedKpiOption({
            label: options[0].label,
            value: options[0].value,
          }),
        );
        if(!filters)
          {
          dispatch(setSelectedKpiTab(options[0].kpiOptions[0]));
        }
      }
    };

    fetchFilteringData();
  }, [programId, dispatch]);

  useEffect(() => {
    if (!programId) return;
    getSubProgramsData();
  }, [programId]);

  const getSubProgramsData = async () => {
    try {
      const res = await getCall(`program/${programId}`, undefined, PORTAL);
      dispatch(setProgramName(res?.data?.data?.name));
      setProgramTypeKey(res.data.data?.type?.key || "");
      setProgramCode(res.data.data?.code || "");
      setProgramStartsAtDate(res.data.data?.startsAt || "");
      const data = res?.data?.data?.groupedPrograms || [];
      const transformedData = data.map(({ id, name, startsAt }) => {
        return { id, name, startsAt };
      });
      console.log(data, "sub programs data");
      transformedData.push(
        { id: "hold", name: "Hold" },
        { id: "yet-to-decide", name: "YTD" },
      );
      setSubProgramsData(transformedData);
      setIsSubProgramsLoaded(true); // Set flag to true after data is loaded
    } catch (error) {
      console.error("Error fetching sub-programs:", error);
      setIsSubProgramsLoaded(false);
    }
  };

  // Utility Functions
  const fetchAllocatedProgramId = async (seekerId: string | number) => {
    const url = `${endPoints.RegisteredSeekersList}/${seekerId}`;
    const response = await getCall(url, undefined, PORTAL);
    return Number(response?.data?.data?.allocatedProgram?.id);
  };
  const handleReject = async (id: any, seekerName: string) => {
    if (
      !window.confirm(
        `Are you sure you want to move ${seekerName[0]} to on-hold?`,
      )
    )
      return;
    const payload = {
      registrationId: Number(id),
      approvalStatus: "rejected",
      rejectionReason: "",
      updatedBy: getItemInLocalStorage("seekerDetails")?.id,
    };

    try {
      setLoading(true);
      const response = await ApiService.blessUser(id, payload);
      if (response.data.statusCode === 200) {
        fetchSeekersList();
      } else {
        alert(response.data.message || "Failed to hold user");
      }
    } catch (error) {
      alert("Failed to hold user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleHold = async (id: any, seekerName: string) => {
    if (
      !window.confirm(`Are you sure you want to move ${seekerName[0]} to YTD?`)
    )
      return;
    const payload = {
      registrationId: Number(id),
      approvalStatus: "on_hold",
      rejectionReason: "",
      updatedBy: getItemInLocalStorage("seekerDetails")?.id,
    };

    try {
      setLoading(true);
      const response = await ApiService.blessUser(id, payload);
      if (response.data.statusCode === 200) {
        fetchSeekersList();
      } else {
        alert(response.data.message || "Failed to move user to YTD");
      }
    } catch (error) {
      alert("Failed to move user to Ytd. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getOptions = (
    id: string,
    row: any,
    onAddReview?: (row: any) => void,
    onSwap?: (row: any) => void,
    onInvoiceSend?: (seekerId: any) => void,
    disabled?: boolean,
  ) => {
    // Extract preferred programs for this row/user
    const preferredPrograms = (row.preference || [])
      .filter((item: any) => item.preferredProgram)
      .map((item: any) => ({
        id: item.preferredProgram.id,
        name: item.preferredProgram.name,
      }));
    const userRole = getItemInLocalStorage("seekerDetails")?.role || "";

    const options = [
      {
        label: "View details",
        action: () => handleViewDetails(row),
        imageSource: undefined,
      },
    ];
    const today = new Date();
    const programStartDate = row?.allocatedProgramStartsAt
      ? new Date(row.allocatedProgramStartsAt)
      : null;

    if (
      row?.registrationStatus !== "cancelled" &&
      hasPermission(userRole, "CANCEL_REGISTRATION", "C") &&
      row?.approvalStatus === "rejected" &&
      (programStartDate === null || programStartDate > today)
    ) {
      options.push({
        label: "Cancel registration",
        action: () => {
          setSelectedRow(row);
          setShowCancelOverlay(true);
        },
        imageSource: undefined,
      });
    }
    if (row?.registrationStatus !== "cancelled") {
      // Add/Update Review
      if (hasPermission(userRole, "ADD_REVIEW", "C")) {
        const hasReview = row.rmComments !== "";
        options.push({
          label: hasReview ? "Update review" : "Add a review",
          action: () => onAddReview && onAddReview(row),
          imageSource: undefined,
        });
      }
      if (hasPermission(userRole, "ADD_SEEKER_TAG", "C")) {
        options.push({
          label: row.userProgramExperiences && row.userProgramExperiences.length > 0
            ? "Update seeker experiences"
            : "Add seeker experiences",
          action: () => {
            setSelectedRow(row);
            setActiveOverlayType("seekerTag");
          },
          imageSource: undefined,
        });
      }
      // Swap Request
      if (
        hasPermission(userRole, "REQ_SWAP_SEEKER", "C") &&
        row.approvalStatus === "approved"
      ) {
        options.push({
          label: "Swap request",
          action: () => onSwap && onSwap(row),
          imageSource: undefined,
          disabled: row.isSwapRequestActive,
        });
      }
    }

    // Send Invoice
    if (
      hasPermission(userRole, "SEND_INVOICE", "C") &&
      row?.paymentStatus?.toLowerCase().includes("completed")
    ) {
      options.push({
        label: "Send invoice",
        action: () => onInvoiceSend && onInvoiceSend(row.seekerId || id),
        imageSource: undefined,
      });
      options.push({
        label: "Download invoice",
        action: () => {
          const startsAt = programStartsAtDate ? new Date(programStartsAtDate) : undefined;
          const years = startsAt ? formateYearDate(startsAt) : '';
          const fileName =  `${programTypeKey === 'PT_HDBMSD' ? 'HDB/MSD' : programCode} ${years} Invoice - ${row.invoiceNumber}`
          downloadInvoice && downloadInvoice(row.invoiceUrl, fileName);
        },
        imageSource: undefined,
      });
    }

    // Bless, Reject, Hold (for mahatria)
    if (
      userRole === "mahatria" ||
      (userRole == "shoba" && row?.approvalStatus !== "cancelled")
    ) {
      switch (row?.approvalStatus) {
        case "on_hold":
          options.push(
            {
              label: "Bless",
              action: () => {
                setBlessUser(row.seekerName);
                setSelectedRow(row);
                setDynamicProgramOptions(preferredPrograms);
                setShowBlessCard(true);
              },
              imageSource: undefined,
            },
            {
              label: "Hold",
              action: () => handleReject(id, row.seekerName),
              imageSource: undefined,
            },
          );
          break;
        case "approved":
          options.push(
            // {
            //   label: "ytd",
            //   action: () => handleHold(id, row.seekerName),
            //   imageSource: undefined,
            // },
            {
              label: "hold",
              action: () => handleReject(id, row.seekerName),
              imageSource: undefined,
            },
          );
          break;
        case "rejected":
          options.push(
            {
              label: "Bless",
              action: () => {
                setBlessUser(row.seekerName);
                setSelectedRow(row);
                setDynamicProgramOptions(preferredPrograms);
                setShowBlessCard(true);
              },
              imageSource: undefined,
            },
            // {
            //   label: "YTD",
            //   action: () => handleHold(id, row.seekerName),
            //   imageSource: undefined,
            // },
          );
          break;
        default:
          if (row?.approvalStatus !== "cancelled") {
            options.push(
              {
                label: "Bless",
                action: () => {
                  setBlessUser(row.seekerName);
                  setSelectedRow(row);
                  setShowBlessCard(true);
                },
                imageSource: undefined,
              },
              {
                label: "Hold",
                action: () => handleReject(id, row.seekerName),
                imageSource: undefined,
              },
              {
                label: "YTD",
                action: () => handleHold(id, row.seekerName),

                imageSource: undefined,
              },
            );
          }
      }
    }

    // Download Proforma Invoice
    if (
      hasPermission(userRole, "DOWNLOAD_PROFORMA", "C") &&
      row.proFormaInvoicePdfUrl
    ) {
      options.push({
        label: "Download proforma invoice",
        action: () => {
          downloadPdf(row.proFormaInvoicePdfUrl);
        },
        imageSource: undefined,
      });
    }
    return options;
  };
  const [showBlessCard, setShowBlessCard] = useState(false);
  const [blessUser, setBlessUser] = useState("");

  const isRowHighlighted = (row: any) => {
    // Mandatory check - don't proceed if subPrograms data isn't loaded
    if (!isSubProgramsLoaded || !subProgramsData || subProgramsData.length === 0) {
      return false;
    }
    const programDate = new Date(row?.allocatedProgramStartsAt);
    const travelDate =( row?.travelUpdatedAt && row?.approvalStatus === 'approved')
      ? new Date(row.travelUpdatedAt)
      : null;
    if (!travelDate) return false;

    const threeDaysBefore = new Date(
      programDate.getTime() - 3 * 24 * 60 * 60 * 1000,
    );
    return travelDate <= programDate && travelDate >= threeDaysBefore;
  };
  const buildDynamicColumns = (tableHeaders: any[]) => {
    const filteredHeaders = tableHeaders.filter(
      (header: any) =>
        header.key !== "profileUrl" &&
        header.label?.toLowerCase() !== "profile url" &&
        header.key !== "rmComments" &&
        header.key !== "age" &&
        header.key !== "gender" &&
        header.key !== "location" &&
        header.key !== "recommendationComments" &&
        header.key !== "cancellationComments",
    );

    const handleImageClick = (e: React.MouseEvent, row: any) => {
      e.stopPropagation(); // This prevents the row click event from firing
      console.log("Image clicked, opening preview for:", row.profileUrl);
      setPreviewImageUrl({
        image: row.profileUrl || defaultProfileIcon,
        altText: row.name || "Profile Image",
      });
      setOpen(true);
    };

    const cols = filteredHeaders.map((header: any) => {
      if (header.key === "blessedWith") {
        return {
          field: header.key,
          headerName: header.label,
          sortable: header.sortable,
          width: 160,
          renderCell: (params: any) => {
            const highlightStyle =
              isSubProgramsLoaded && isRowHighlighted(params.row)
                ? { color: "#e28619" }
                : {};
            const isSwapRequestActive =
              params.row?.isSwapRequestActive === true;
            const blessedWith = params.row.blessedWith;
            let displayValue;

            if (
              blessedWith === null ||
              blessedWith === undefined ||
              blessedWith === ""
            ) {
              if (params.row.approvalStatus === "pending") {
                displayValue = "Unassigned";
              } else if (params.row.approvalStatus === "rejected") {
                displayValue = "Hold";
              } else if (params.row.approvalStatus === "on_hold") {
                displayValue = "YTD";
              } else {
                displayValue = params.row.approvalStatus;
              }
            } else {
              displayValue =
                Object.keys(highlightStyle).length > 0
                  ? blessedWith
                  : colorizeMahatriaInfinitheism(blessedWith);
            }
            return (
              <div className={styles.blessedWithContainer}>
                <span
                  className={
                    displayValue === "cancelled" ||
                    displayValue === "Unassigned"
                      ? `${styles.blessedWithText} ${styles.blessedWithTextCancelled}`
                      : styles.blessedWithText
                  }
                  style={highlightStyle}
                >
                  {displayValue}
                </span>
                {(params.row.wantsSwapReqActive ||
                  params.row.canShiftReqActive) && (
                  <span className={styles.basicDetailsSeparator}>|</span>
                )}
                {params.row.wantsSwapReqActive && (
                  <img
                    src={shuffle}
                    alt="Wants Swap"
                    className={styles.swapIcon}
                    title="Wants swap"
                  />
                )}
                {params.row.canShiftReqActive && (
                  <span className={styles.canShiftText}>can shift</span>
                )}
              </div>
            );
          },
        };
      }
      if (header.label === "HDBs") {
        return {
          field: header.key,
          headerName: header.label,
          sortable: header.sortable,
          width: 80,
          // renderHeader: (params: any) => (
          //   <span>
          //     {colorizeMahatriaInfinitheism(params.colDef.headerName)}
          //   </span>
          // ),
          renderCell: (params: any) => {
            const value = params.row[header.key] ?? "-";
            // Only call isRowHighlighted if subPrograms data is loaded
            const highlightStyle =
              isSubProgramsLoaded && isRowHighlighted(params.row)
                ? { color: "#e28619" }
                : {};

            const baseSpanStyle: React.CSSProperties = {
              maxWidth: 140,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "inline-block",
              verticalAlign: "middle",
              ...highlightStyle,
            };

            return (
              <span
                style={baseSpanStyle}
                title={typeof value === "string" ? value : ""}
              >
                {value === null || value === undefined || value === ""
                  ? "-"
                  : value}
              </span>
            );
          },
        };
      }
      if (header.key === "recommendation") {
        return {
          field: header.key,
          headerName: header.label,
          sortable: header.sortable,
          width: 180,
          renderCell: (params: any) => {
            const highlightStyle =
              isSubProgramsLoaded && isRowHighlighted(params.row)
                ? { color: "#e28619" }
                : {};
            const value = params.row[header.key] ?? "-";
            const comment = params.row.recommendationComments ?? "";
            const tooltipContent = (
              <p className={styles.reviewText}>
                {comment
                  ? comment
                      .split("\n")
                      .map((line: string, idx: number, arr: string[]) => (
                        <React.Fragment key={idx}>
                          {line}
                          {idx < arr.length - 1 && <br />}
                        </React.Fragment>
                      ))
                  : ""}
              </p>
            );
            return (
              <Tooltip title={tooltipContent}>
                <span
                  style={{
                    maxWidth: 160,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "inline-block",
                    ...highlightStyle,
                  }}
                >
                  {value === null || value === undefined || value === ""
                    ? "-"
                    : value}
                </span>
              </Tooltip>
            );
          },
        };
      }
      if (header.key === "cancellationReason") {
        return {
          field: header.key,
          headerName: header.label,
          sortable: header.sortable,
          width: 180,
          renderCell: (params: any) => {
            const value = params.row[header.key] ?? "-";
            const comment = params.row.cancellationComments ?? "";
            const tooltipContent = (
              <p className={styles.reviewText}>
                {comment
                  ? comment
                      .split("\n")
                      .map((line: string, idx: number, arr: string[]) => (
                        <React.Fragment key={idx}>
                          {line}
                          {idx < arr.length - 1 && <br />}
                        </React.Fragment>
                      ))
                  : ""}
              </p>
            );
            return (
              <Tooltip title={tooltipContent}>
                <span
                  style={{
                    maxWidth: 160,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "inline-block",
                  }}
                >
                  {value === null || value === undefined || value === ""
                    ? "-"
                    : value}
                </span>
              </Tooltip>
            );
          },
        };
      }
      return {
        field: header.key,
        headerName: header.label,
        sortable: header.sortable,
        width: ["rmComments", "paymentStatus"].includes(header.key)
          ? 200
          : ["seekerName", "fullName", "name", "travelUpdatedAt"].includes(
                header.key,
              )
            ? 230
            : 130,

        renderCell: (params: any) => {
          const value = params.row[header.key] ?? "-";
          // Only call isRowHighlighted if subPrograms data is loaded
          const highlightStyle =
            isSubProgramsLoaded && isRowHighlighted(params.row)
              ? { color: "#e28619" }
              : {};

          const baseSpanStyle: React.CSSProperties = {
            maxWidth: 140,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "inline-block",
            verticalAlign: "middle",
            ...highlightStyle,
          };

          if (header.key === "travelUpdatedAt") {
            return (
              <span
                style={baseSpanStyle}
                title={typeof value === "string" ? formatDateString(value) : ""}
              >
                {value === null || value === undefined || value === ""
                  ? "-"
                  : formatDateString(value)}
              </span>
            );
          }

          if (header.key === "averageRating") {
            const avg = params.row.averageRating;
            const hasNoRating =
              avg === null ||
              avg === undefined ||
              avg === "" ||
              avg === 0 ||
              avg === "0.00";
            const hasComment =
              params.row.rmComments && params.row.rmComments !== "";

            // Tooltip content for RM comment
            const tooltipContent = (
              <p className={styles.reviewText}>
                {params.row.rmComments
                  ? params.row.rmComments
                      .split("\n")
                      .map((line: string, idx: number, arr: string[]) => (
                        <React.Fragment key={idx}>
                          {line}
                          {idx < arr.length - 1 && <br />}
                        </React.Fragment>
                      ))
                  : ""}
              </p>
            );

            return (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  ...highlightStyle,
                }}
              >
                {hasNoRating ? (
                  <>
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "47px",
                        height: "28px",
                      }}
                    >
                      -
                    </span>
                  </>
                ) : (
                  <>
                    {hasComment ? (
                      <Tooltip title={tooltipContent}>
                        <span className={styles.ratingCell}>
                          <img src={starIcon} alt="star" />
                          <span>{avg}</span>
                        </span>
                      </Tooltip>
                    ) : (
                      <>
                        <img src={starIcon} alt="star" />
                        <span>{avg}</span>
                      </>
                    )}
                  </>
                )}
              </span>
            );
          }
          if (["seekerName", "fullName", "name"].includes(header.key)) {
            // Truncate name if longer than 22 chars
            const displayName =
              typeof value === "string" && value.length > 22
                ? value.slice(0, 22) + "…"
                : value;
            return (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  ...highlightStyle,
                }}
                title={value}
              >
                <>
                  <div className={styles.imageContainer}>
                    <img
                      src={params.row.profileUrl || defaultProfileIcon}
                      alt={value}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        objectFit: "cover",
                        marginRight: 8,
                        background: "#f0f0f0",
                        border: "1px solid #eee",
                        cursor: "pointer",
                      }}
                      onClick={(e) => handleImageClick(e, params.row)}
                    />
                    <div
                      className={styles.hoverEyePreview}
                      onClick={(e) => handleImageClick(e, params.row)}
                    ></div>
                  </div>
                    <span 
                      title={value}
                      className={styles.seekerNameText}>
                                                       
                        {value}
                    </span>
                </>
              </span>
            );
          }

          // Default renderingf
          return (
            <span
              style={baseSpanStyle}
              title={typeof value === "string" ? value : ""}
            >
              {value === null || value === undefined || value === "" ? (
                <span
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "70px",
                    height: "28px",
                    ...highlightStyle,
                  }}
                >
                  -
                </span>
              ) : (
                value
              )}
            </span>
          );
        },
      };
    });

    // Add the Basic Details column
    cols.splice(1, 0, {
      field: "basicDetails",
      headerName: "Basic Details",
      sortable: false,
      width: 200,
      renderCell: (params: any) => {
        const highlightStyle =
          isSubProgramsLoaded && isRowHighlighted(params.row)
            ? { color: "#e28619" }
            : {};

        const age =
          params.row.age !== undefined && params.row.age !== null
            ? params.row.age
            : "-";
        const gender = params.row.gender
          ? params.row.gender.toLowerCase().startsWith("f")
            ? "F"
            : params.row.gender.toLowerCase().startsWith("m")
              ? "M"
              : params.row.gender.charAt(0).toUpperCase()
          : "-";
        const location = params.row.location || "-";
        return (
          <span
            style={{
              maxWidth: 200,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",

              display: "inline-block",
              verticalAlign: "middle",
              ...highlightStyle,
            }}
            title={`${age} | ${gender} | ${location}`}
          >
            {age}
            <span className={styles.basicDetailsSeparator}>|</span>
            {gender}
            <span className={styles.basicDetailsSeparator}>|</span>
            {location}
          </span>
        );
      },
    });

    cols.push({
      field: "menu",
      headerName: "",
      width: 80,
      pinned: "right",
      renderCell: (params: any) => (
        <div className={styles.menuColumnCell}>
          {/* <div
            className={styles.viewIconContainer}
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(params.row);
            }}
            title="View details"
          >
            <img
              src={ViewDetailsIcon}
              alt="View details"
              className={styles.viewIcon}
            />
          </div> */}
          <KebabMenu
            items={getOptions(
              params.row.id,
              params.row,
              handleAddReview,
              handleOpenSwap,
              handleSendInvoice,
            )}
            menuContainerClassname={styles.menuTooltip}
          />
        </div>
      ),
    });

    return cols;
  };
// Event Handlers
const handleTabChange = (count: number, status: string, index: number) => {
  const kpiIndex = kpis.findIndex((item) => item.label === status);
  const kpi = kpis[kpiIndex];
  if (kpi) {
    setCurrentPage(1);
    dispatch(setActiveTab(index));
    setAppliedFilters({});
    // dispatch(
    //   setSelectedKpiFilter({
    //     kpiCategory: kpi.kpiCategory,
    //     kpiFilter: kpi.kpiFilter,
    //   }),
    // );
    setSearchValue({ value: "", open: false });
    setSearchQuery("");
  }
};

const handleRowClick = (row: any) => {
  if (row.id && row.programId) {
    // navigate(
    //   `/admin/action-cards/registered/seeker-details/${row.id}/${row.programId}`,
    // );
    setSelectedRow(row);
    setActiveOverlayType("quickView");
    // setFlippedUserId(row.id);
  }
};
const handleViewDetails = (row: any) => {
  if (row.id && row.programId) {
    navigate(
      `/admin/action-cards/registered/seeker-details/${row.id}/${row.programId}`,
    );
   
  }
};
const handleAddReview = async (row: any) => {
  setSelectedRow(row);
  setActiveOverlayType("review");
  const ratingsObj: { [key: string]: { rating: number; id?: number } } = {};
  if (Array.isArray(row.ratings)) {
    row.ratings.forEach((r: any) => {
      ratingsObj[r.ratingKey] = { rating: r.rating, id: r.id };
    });
  }
  setReviewInitialRatings(ratingsObj);
  setReviewInitialComments(row.rmComments || "");
  setLoading(true); // Enable loader before API call
  try {
    const res = await getCall(`registration/${row.id}`, undefined, PORTAL);
    const recommendationsArr = res?.data?.data?.recommendation || [];
    let latestRecommendation = null;
    if (Array.isArray(recommendationsArr) && recommendationsArr.length > 0) {
      latestRecommendation = recommendationsArr.reduce((latest, curr) => {
        if (!latest) return curr;
        return curr.id > latest.id ? curr : latest;
      }, null);
    }
    setReviewInitialRecommendations(latestRecommendation);
    setPrevRating(res?.data?.data?.prevRating || {});
  } catch {
    setReviewInitialRecommendations(null);
  } finally {
    setLoading(false); // Disable loader after API call
  }
};

const handleSaveReview = (result: { success: boolean; message?: string }) => {
  setActiveOverlayType(null);
  setSelectedRow(null);
};
const onRefresh = () => {
  fetchSeekersList();
};
const updateRowSwapStatus = (rowId: string | number, isActive: boolean) => {
  setData((prevData) =>
    prevData.map((item) =>
      item.id === rowId ? { ...item, isSwapRequestActive: isActive } : item,
    ),
  );
};
const handleOpenSwap = async (row: any) => {
  setSelectedRow(row);
  setActiveOverlayType("swap");
  try {
    const response = await getCall(`${endPoints.program}/${row.programId}`, undefined, PORTAL);
    const data = response?.data?.data;
    const allocatedId = await fetchAllocatedProgramId(row.id);
    let options: { value: number; label: string; disabled?: boolean }[] = [];

    if (data?.type?.isGroupedProgram) {
      options =
        data.groupedPrograms?.map((item: any) => ({
          value: Number(item.id),
          label: item.name,
          disabled: Number(item.id) === allocatedId,
        })) || [];
    } else {
      options =
        data.sessions?.map((item: any) => ({
          value: Number(item.id),
          label: item.name,
          disabled: Number(item.id) === allocatedId,
        })) || [];
    }
    setDynamicProgramOptions(options);
  } catch {
    setDynamicProgramOptions([]);
  }
};

const handleSendInvoice = (seekerId: any) => {
  if (seekerId) {
    getCall(`invoice/sendto/${seekerId}`, undefined, PORTAL);
  }
};

const downloadInvoice = async (url: string, name: string | undefined) => {
  if (!url) return;
  try {
    const response = await fetch(url, { method: "GET" });
    if (!response.ok) throw new Error("Network response was not ok");
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = name ?? "invoice" + ".pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    alert("Failed to download invoice.");
  }
};

const handleRemoveFilter = (key: string, valueToRemove: string) => {
  setAppliedFilters((prevFilters) => {
    const updatedFilters = { ...prevFilters };

    if (updatedFilters[key]) {
      if (Array.isArray(updatedFilters[key].value)) {
        // Handle array of value-label objects
        const newValues = updatedFilters[key].value.filter(
          (item: { value: string; label: string }) =>
            item.label !== valueToRemove && item.value !== valueToRemove
        );

        if (newValues.length === 0) {
          delete updatedFilters[key];
        } else {
          updatedFilters[key] = {
            ...updatedFilters[key],
            value: newValues
          };
        }
      } else if (typeof updatedFilters[key].value === "object") {
        // Handle objects like dateRange or numberOfHdbs
        if (
          "startDate" in updatedFilters[key].value ||
          "operator" in updatedFilters[key].value
        ) {
          delete updatedFilters[key];
        }
      }
    }

    return updatedFilters;
  });
};

const handleClearAllFilters = () => {
  setAppliedFilters({});
  setShowFilterOverlay(false);
};

useEffect(() => {
  const params = new URLSearchParams(location.search);
  const parentFilterParam = params.get("parentFilter");
  const selectedKpiFilterParam = params.get("selectedKpiFilter");


  if (parentFilterParam) {
    const parsedFilter = JSON.parse(parentFilterParam);
    dispatch(setSelectedKpiOption(parsedFilter));
  }
  if (selectedKpiFilterParam) {
    const parsedKpiFilter = JSON.parse(selectedKpiFilterParam);
    dispatch(setSelectedKpiTab(parsedKpiFilter));
  }
}, [location.search]);
const transformSessionData = (res)=>{}
const fetchSeekersList = async (filtersTabObj?: any,
  sortKey?: string = sortState.sortKey,
  sortOrder?: string = sortState.sortOrder
) => {
  if (initalLoadingRef.current) {
    setLoading(true);
    initalLoadingRef.current = false;
  } else {
    setIsintialLoading(true);
  }
  try {
    let url = `${endPoints.registeredSeekerList}?limit=${pageSize}&offset=${(currentPage - 1) * pageSize}`;
    if (programIdNumber) url += `&programId=${programIdNumber}`;

    // Add parentFilter if selectedKpiOption exists
    if (selectedKpiOption?.value) {
      url += `&parentFilter=${selectedKpiOption.value}`;
    } else {
      return;
    }
    if (sortKey) {
      url += `&sortKey=${sortKey}&sortOrder=${sortOrder}`;
    }

    const filtersObj: any = filtersTabObj ?? {};
    if (!filtersTabObj) {
      if (sessionId && !hasSyncedTab) {
        if (sessionId === "hold") {
          filtersObj.kpiCategory = "registrations";
          filtersObj.kpiFilter = "rejected";
        } else if (["waitlisted", "yet-to-decide"].includes(sessionId)) {
          filtersObj.kpiCategory = "registrations";
          filtersObj.kpiFilter = "onHold";
        } else if (!isNaN(Number(sessionId))) {
          filtersObj.kpiCategory = "programs";
          filtersObj.kpiFilter = `program_${sessionId}`;
         
        }
      } else if (selectedKpiFilter) {
        filtersObj.kpiCategory = selectedKpiFilter.kpiCategory;
        filtersObj.kpiFilter = selectedKpiFilter.kpiFilter;
      } else if (selectedKpiTab) {
        filtersObj.kpiCategory = selectedKpiTab?.kpiCategory;
        filtersObj.kpiFilter = selectedKpiTab?.kpiFilter;
      }
    }
    setBulkEmailUrl({
      programId: programIdNumber,
      filters: selectedKpiFilter,
    });

    // Transform appliedFilters before merging
    const transformedFilters = transformFilters(appliedFilters);

    // Merge with filtersObj
    Object.assign(filtersObj, transformedFilters);

    if (Object.keys(filtersObj).length > 0) {
      url += `&filters=${encodeURIComponent(JSON.stringify(filtersObj))}`;
    }
    if (searchValue.value.trim()) {
      url += `&searchText=${encodeURIComponent(searchValue.value.trim())}`;
    }
    const res = await getCall(url, undefined, PORTAL);
    const rawData = res?.data?.data?.data || [];
    const tableHeaders = res?.data?.data?.tableHeaders || [];
    const pagination = res?.data?.data?.pagination || {};
    const kpis = res?.data?.data?.kpis || [];
    const transformSessionData = (res) =>{
      return res.map(item =>({
          id: item.subProgramId,
          name: item.label,
          totalSeekers: item.totalSeatsCount,
          allocatedCount: item.count,
          organisationUserCount: item.organisationUserCount,
      })
      )}
    setSessionData(transformSessionData(res?.data?.data?.statusCountsData || []));
    const dashboardFilter = res?.data?.data?.filters || {};
    setData(rawData);
    setColumns(buildDynamicColumns(tableHeaders));
    setTotalData(pagination.totalRecords || 0);
    setKpis(kpis);

   let kpiIndex = kpis.findIndex(
      (k: any) => k.label === selectedKpiTab?.label ||
        k.kpiFilter === selectedKpiTab?.kpiFilter,
    );
    if(kpiIndex === -1)
    {
      kpiIndex = 0;
    }
    dispatch(setSelectedKpiTab(kpis[kpiIndex]));

    if (kpiIndex !== -1 && activeTab !== kpiIndex) {
      dispatch(setActiveTab(kpiIndex));
    }
    // setDashboardFilters(dashboardFilter);
  } catch {
    setData([]);
    setColumns([]);
    setTotalData(0);
    setKpis([]);
  } finally {
    setLoading(false);
    setIsintialLoading(false);
    setLoadingState(false);
  }
};

  // Add handler for sort changes
  const handleSort = (sortState: SortState) => {
    setSortState({ ...sortState });
    // Trigger refetch with new sort params
    fetchSeekersList(undefined, sortState.sortKey, sortState.sortOrder);
  };

// 2. Add a useEffect to sync tab from filters ONLY ONCE after kpis are loaded
// useEffect(() => {
//   if (
//     !hasSyncedTab &&
//     filters &&
//     filters.kpiCategory &&
//     filters.kpiFilter 
//   ) {

//       setSelectedKpiFilter({
//         kpiCategory: kpis[tabIndex].kpiCategory,
//         kpiFilter: kpis[tabIndex].kpiFilter,
//       });

//     }

// }, [filters, hasSyncedTab]);

// Effects
useEffect(() => {
  // Only fetch seekers list if subPrograms data is loaded
  if (isSubProgramsLoaded) {
    fetchSeekersList();
  }
}, [
  currentPage,
  pageSize,
  searchQuery,
  // selectedKpiFilter,
  // activeTab,
  appliedFilters,
  selectedKpiOption,
  isSubProgramsLoaded,
]);

useEffect(() => {
  if (!sessionId || kpis.length === 0 || hasSyncedTab) return;

  let kpiIndex = -1;

  if (sessionId === "hold") {
    kpiIndex = kpis.findIndex((k: any) => k.kpiFilter === "rejected");
  } else if (["waitlisted", "yet-to-decide"].includes(sessionId)) {
    kpiIndex = kpis.findIndex((k: any) => k.kpiFilter === "onHold");
  } else if (!isNaN(Number(sessionId))) {
    kpiIndex = kpis.findIndex(
      (k: any) => k.kpiFilter === `program_${sessionId}`,
    );
  }

  if (
    kpiIndex !== -1 &&
    (activeTab !== kpiIndex ||
      !selectedKpiFilter ||
      selectedKpiFilter.kpiFilter !== kpis[kpiIndex].kpiFilter)
  ) {
    dispatch(setActiveTab(kpiIndex));
    // setSelectedKpiFilter({
    //   kpiCategory: kpis[kpiIndex].kpiCategory,
    //   kpiFilter: kpis[kpiIndex].kpiFilter,
    // });
    setHasSyncedTab(true);
  }
}, [sessionId, kpis]);

// When user manually changes tab, allow auto-sync again if sessionId changes
useEffect(() => {
  setHasSyncedTab(false);
}, [sessionId]);

// Render logic
const popup = showFilterOverlay && (
  <div className={styles.overlayBackdrop}>
    <div className={styles.overlayPopup}>
      <button onClick={() => setShowFilterOverlay(false)}>Close</button>
      <AdminFilterOverlay
        displayFilters={dashboardFilters}
        open={showFilterOverlay}
        onClose={() => setShowFilterOverlay(false)}
        onApply={(filters) => {
          const cleanedFilters = Object.fromEntries(
            Object.entries(filters).filter(([key, value]) => {
              // Handle arrays (including location values)
              if (Array.isArray(value?.value)) {
                return value.value.length > 0;
              }
              // Handle string values
              if (typeof value === "string") {
                return value.trim() !== "";
              }
              // Handle objects (like location with label and value)
              if (typeof value === "object" && value !== null) {
                // Special handling for location
                if (key === 'location' && Array.isArray(value.value)) {
                  return value.value.length > 0;
                }
                return true;
              }
              return true;
            })
          );
          setAppliedFilters(cleanedFilters);
          setShowFilterOverlay(false);
          setCurrentPage(1);
        }}
        initialFilters={appliedFilters}
      />
    </div>
  </div>
);

const handleDownload = async (
  reportName: string,
  selectedReport?: string,
) => {
  setShowExportPopup(false);
  try {
    setLoading(true);
    let url = `${endPoints.registeredSeekerList}?limit=${pageSize}&offset=${(currentPage - 1) * pageSize}`;
    if (programIdNumber) url += `&programId=${programIdNumber}`;
    if (selectedKpiOption?.value) {
      url += `&parentFilter=${selectedKpiOption.value}`;
    }
    url += `&downloadType=${selectedReport}`;

    const filtersObj = {};
    if (selectedOption === "filtered") {
    
      
      if (sessionId && !hasSyncedTab) {
        if (sessionId === "hold") {
          filtersObj.kpiCategory = "registrations";
          filtersObj.kpiFilter = "rejected";
        } else if (["waitlisted", "yet-to-decide"].includes(sessionId)) {
          filtersObj.kpiCategory = "registrations";
          filtersObj.kpiFilter = "onHold";
        } else if (!isNaN(Number(sessionId))) {
          filtersObj.kpiCategory = "programs";
          filtersObj.kpiFilter = `program_${sessionId}`;
         
        }
      } else if (selectedKpiFilter) {
        filtersObj.kpiCategory = selectedKpiFilter.kpiCategory;
        filtersObj.kpiFilter = selectedKpiFilter.kpiFilter;
      } else if (selectedKpiTab) {
        filtersObj.kpiCategory = selectedKpiTab?.kpiCategory;
        filtersObj.kpiFilter = selectedKpiTab?.kpiFilter;
      }
      const transformedFilters = transformFilters(appliedFilters);

      // Merge with filtersObj
      Object.assign(filtersObj, transformedFilters);
    }

    url += `&filters=${encodeURIComponent(JSON.stringify(filtersObj))}`;

    // Get the downloadUrl from the same API
    const response = await getCall(url, undefined, PORTAL);
    const downloadUrl = response?.data?.data?.downloadUrl;

    if (downloadUrl) {
      await downloadExcelFromApi({
        url: downloadUrl,
        filePrefix: reportName,
      });
    }
  } catch (error) {
    console.error("Error downloading file:", error);
  }finally{
    setLoading(false);
  }
};

const exportPopup = showExportPopup && (
  <DownloadPopup
    open={showExportPopup}
    onClose={() => setShowExportPopup(false)}
    selected={selectedOption}
    setSelected={setSelectedOption}
    onDownload={handleDownload}
  />
);

const transformedStatsData = kpis.map((item: any) => ({
  status: item.label,
  count: typeof item.value === "number" ? item.value : 0,
}));

const handleBless = async (
  user: UserData,
  sessionId: number,
  sessionType: string,
) => {
  const seekerDetails = getItemInLocalStorage("seekerDetails");
  let payload = {
    allocatedProgramId: sessionId,
    registrationId: Number(user.registrationId),
    approvalStatus: "approved",
    approvalDate: new Date().toISOString(),
    approvedBy: seekerDetails.id,
    updatedBy: seekerDetails.id,
  };

  if (sessionType === "hold" || sessionType === "Hold") {
    payload = {
      registrationId: Number(user.registrationId),
      approvalStatus: "rejected",
      rejectionReason: "",
      updatedBy: seekerDetails.id,
    };
  } else if (sessionType === "yet-to-decide" || sessionType === "YTD") {
    payload = {
      registrationId: Number(user.registrationId),
      approvalStatus: "on_hold",
      rejectionReason: "",
      updatedBy: seekerDetails.id,
    };
  }

  try {
    setLoading(true);
    const response = await ApiService.blessUser(
      Number(user.registrationId),
      payload,
    );
    if (response.data.statusCode === 200) {
      setShowBlessCard(false);
      fetchSeekersList();
    } else {
      alert(response.data.message || "Failed to bless user");
    }
  } catch (error) {
    console.error("Error blessing user:", error);
    alert("Failed to bless user. Please try again.");
  } finally {
    setLoading(false);
  }
};

const handleBlessed = async (
  seekerId: number,
  targetProgramId: number,
  swapType: string,
  swapRequestId?: number,
) => {
  try {
    setLoading(true);
    let payload;
    let response;

    if (swapRequestId) {
      // Handle existing swap request
      payload = {
        status:
          targetProgramId === "hold"
            ? "rejected"
            : targetProgramId === "yet-to-decide"
              ? "on_hold"
              : "approved",
        comment: "",
        movingSeekerRegistrationId: Number(seekerId),
        ...(swapType === "move" &&
          targetProgramId !== "hold" &&
          targetProgramId !== "yet-to-decide" && {
          movingToSubProgramId: targetProgramId,
        }),
        swappingType: swapType,
      };
      response = await ApiService.updateSwapRequest(swapRequestId, payload);
    } else {
      // Handle normal blessing
      payload = {
        allocatedProgramId:
          targetProgramId === "hold" || targetProgramId === "yet-to-decide"
            ? null
            : targetProgramId,
        registrationId: Number(seekerId),
        approvalStatus:
          targetProgramId === "hold"
            ? "rejected"
            : targetProgramId === "yet-to-decide"
              ? "on_hold"
              : "approved",
        approvalDate: new Date().toISOString(),
        approvedBy: getItemInLocalStorage("seekerDetails")?.id,
        updatedBy: getItemInLocalStorage("seekerDetails")?.id,
      };
      response = await ApiService.blessUser(seekerId, payload);
    }

    if (response.data.statusCode === 200) {
      setShowBlessCard(false);
      fetchSeekersList();
    } else {
      alert(response.data.message || "Failed to process request");
    }
  } catch (error) {
    console.error("Error in handleBlessed:", error);
    alert("Failed to process request. Please try again.");
  } finally {
    setLoading(false);
  }
};
const sendBulkEmail = showBulkEmailPopup && (
  <BulkEmailsPopUp
    open={showBulkEmailPopup}
    headers={columns}
    totalData={totalData}
    loading={loading}
    onRowClick={handleRowClick}
    isheight={true}
    rowHeight={60}
    onClose={() => setShowBulkEmailPopup(false)}
    bulkEmailUrlData={bulkEmailUrl}
    programKey={programTypeKey}
    kpiFilter={
      kpis.find((item) => item.kpiFilter === selectedKpiFilter?.kpiFilter)
        ?.communicationCategory || "hdb_all"
    }
  />
);


return (
  <div className={styles.container}>
    {loading || loadingState ? (
      <Loader type="large" />
    ) : (
      <div className={styles.dataGridContainer}>
        <AdminKpiData
          className={styles.adminKpiData}
          handleTabChange={handleTabChange}
          // activeTab={activeTab}
          tabsData={transformedStatsData}
          role="admin"
          // setActiveTab={setActiveTab}
          kpis={kpis}
        />

        <DashboardHeader
          searchValue={searchValue}
          title={kpis[activeTab]?.label || ""}
          enableFilter={true}
          appliedFilters={appliedFilters}
          setSearchValue={setSearchValue}
          onSearch={(val) => {
            setSearchQuery(val);
            setCurrentPage(1);
          }}
          activeTab={activeTab}
          enableExport={true}
          onExport={() => setShowExportPopup(true)}
          onSearchValueChange={(value: string) =>
            setSearchValue((prev) => ({ ...prev, value }))
          }
          onFilterClick={() => setShowFilterOverlay(true)}
          filterPills={
            <FilterPills
              appliedFilters={appliedFilters}
              totalData={totalData}
              handleRemoveFilter={handleRemoveFilter}
              handleClearAllFilters={handleClearAllFilters}
              onMoreClick={() => setShowFilterOverlay(true)}
              totalDataLength={totalData}
            />
          }
          onEmailClick={() => {
            setShowBulkEmailPopup(showBulkEmailPopup ? false : true);
          }}
          totalDataLength={totalData}
          setSelectedOption={setSelectedOption}
          selectedOptions={selectedOption}
          handleDownload={handleDownload}
        />
        {sendBulkEmail}
        {exportPopup}
        {popup}
        <DataGridWithPagination
          headers={columns}

          seekersData={data.map((items) => ({
            ...items,
            seekerName: colorizeMahatriaInfinitheism(items.seekerName),
            __highlight: isRowHighlighted(items),
          }))}
          totalData={totalData}
          pageSize={pageSize}
          setPageSize={setPageSize}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          loading={isIntialLoading}
          onRowClick={handleRowClick}
          isheight={true}
          rowHeight={60}
          slots={{
            noRowsOverlay: () => <div>{noData}</div>,
          }}
          onSortChange={handleSort}
          isSort = {true}
        />
        {activeOverlayType === "review" && (
          <ReviewOverlay
            open={true}
            onClose={() => setActiveOverlayType(null)}
            onSave={() => handleSaveReview}
            seekerId={selectedRow?.id}
            programId={programIdNumber}
            initialRatings={reviewInitialRatings}
            initialComments={reviewInitialComments}
            prevRating={prevRating}
            seekersData={selectedRow}
            disablePreferences={true}
            onRefresh={onRefresh}
            initialRecommendations={reviewInitialRecommendations}
          />
        )}
        {activeOverlayType === "swap" && (
          <SwapFilterOverlay
            open={true}
            swapTypeOptions={swapTypeOptions}
            programOptions={dynamicProgramOptions}
            seekerId={selectedRow?.id ? Number(selectedRow?.id) : undefined}
            initialData={selectedRow}
            onUpdate={() => {
              setActiveOverlayType(null);
              onRefresh();
              updateRowSwapStatus(selectedRow?.id, true);
            }}
            onCancel={() => setActiveOverlayType(null)}
            isLoading={loading}
            seekerData={selectedRow}
          />
        )}
        {activeOverlayType === "quickView" && (
          <QuickViewOverlay
            open={true}
            onClose={() => setActiveOverlayType(null)}
            seekersData={selectedRow}
          />
        )}
        {activeOverlayType === "seekerTag" && (
          <SeekerTags
            open={true}
            onClose={() => setActiveOverlayType(null)}
            onSave={(tags) => {
              setActiveOverlayType(null);
            }}
            onRefresh={onRefresh}
            initialTags={selectedRow?.userProgramExperiences || []}
            seekerData={selectedRow}
          />
        )}
        {showBlessCard && (
          <UserCardOverlay
            user={transformSeekerResponse(selectedRow)} // Transform the selected row data
            getInitials={(name: string) =>
              name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
            }
            sessionId={programId}
            handleBless={handleBless}
            handleBlessed={handleBlessed}
            isFlipped={flippedUserId === selectedRow?.id}
            onFlip={() => setFlippedUserId(selectedRow?.id)}
            onCloseFlip={() => {
              setShowBlessCard(false);
              setFlippedUserId(null);
            }}
            programsList={dynamicProgramOptions}
            allocatedProgramId={selectedRow?.allocatedProgramId}
            highlightAllocated={false}
              handleShowAllMatches={(name) => {}} // Add if needed
            setShowOverlay={setShowBlessCard}
            fetchApprovedUsersForSession={fetchSeekersList}
              setIsCardClicked={() => {}}
            selectedSwapSeeker={null}
              setExcludedUserId={() => {}}
              setSelectedSwapSeeker={() => {}}
              setCustomTitle={() => {}}
            sessions={sessionData}
            selectedFilter={{}}
            isOverlay={true}
            setShowBlessCard={setShowBlessCard}
            onBlessSuccess={() => {
              setShowBlessCard(false);
              fetchSeekersList();
            }}
          />
        )}
        {showCancelOverlay && selectedRow && (
          <CancelRegistrationOverlay
            onClose={(flag: boolean) => {
              if (flag) {
                fetchSeekersList();
              }
              setShowCancelOverlay(false);
              setSelectedRow(null);
            }}
            seekersData={selectedRow}
            setLoadingState={setLoadingState}
          />
        )}
      </div>
    )}

    <ImagePreview 
      imageUrl={previewImageUrl?.image} 
      altText={previewImageUrl?.altText || "Profile Image"} 
      setOpen={setOpen} 
      isOpen={open} 
      width={600}
      height={400}
    />
  </div>
);
};

export default HdbSeekersListAdmin;
